<script setup>
import { Check } from 'lucide-vue-next';

/** A large selectable row, used for recipients, bank accounts and options */
const model = defineModel({ type: [String, Number], default: null });
defineProps({
  value: { type: [String, Number], required: true },
  name: { type: String, required: true },
  title: { type: String, required: true },
  meta: { type: String, default: '' },
  icon: { type: [Object, Function], default: null },
  disabled: { type: Boolean, default: false },
});
</script>

<template>
  <label class="pd-rc" :class="{ 'is-on': model === value, 'is-disabled': disabled }">
    <input v-model="model" type="radio" class="pd-sr" :name="name" :value="value" :disabled="disabled" />
    <span v-if="icon || $slots.lead" class="pd-rc__lead">
      <slot name="lead"><component :is="icon" :size="22" :stroke-width="2" aria-hidden="true" /></slot>
    </span>
    <span class="pd-rc__body">
      <span class="pd-rc__title">{{ title }}</span>
      <span v-if="meta" class="pd-rc__meta">{{ meta }}</span>
      <slot />
    </span>
    <span class="pd-rc__mark" aria-hidden="true"><Check :size="14" :stroke-width="3.5" /></span>
  </label>
</template>

<style scoped>
.pd-rc {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
  border: 1px solid var(--pd-line);
  border-radius: var(--pd-r-md);
  background: var(--pd-white);
  cursor: pointer;
  transition: border-color var(--pd-fast) var(--pd-ease), background-color var(--pd-fast) var(--pd-ease);
}
@media (hover: hover) { .pd-rc:hover { border-color: var(--pd-line-strong); } }
.pd-rc.is-on { border-color: var(--pd-line-active); background: var(--pd-tint); }
.pd-rc:has(input:focus-visible) { box-shadow: var(--pd-ring); }
.pd-rc.is-disabled { opacity: 0.55; cursor: not-allowed; }
.pd-rc__lead { display: grid; place-items: center; flex: none; width: 52px; height: 52px; border-radius: 14px; background: var(--pd-tint); color: var(--pd-orange-text); }
.pd-rc.is-on .pd-rc__lead { background: var(--pd-orange); color: var(--pd-on-orange); }
.pd-rc__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.pd-rc__title { font-weight: 800; font-size: 1.0625rem; letter-spacing: -0.01em; }
.pd-rc__meta { font-size: 0.8125rem; color: var(--pd-muted); }
.pd-rc__mark { display: grid; place-items: center; flex: none; width: 26px; height: 26px; border-radius: 50%; border: 1px solid var(--pd-line-strong); color: transparent; }
.pd-rc.is-on .pd-rc__mark { background: var(--pd-ink); border-color: var(--pd-ink); color: var(--pd-white); }
</style>
